import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useEffect } from 'react';

interface NumberCounterProps {
  value: number;
  /** Number of decimal places. Default 0 — register amounts are whole DKK. */
  decimals?: number;
  /** Group thousands with thin space (Danish convention). Default true. */
  group?: boolean;
  /** Animation duration in seconds. Default 0.6. */
  duration?: number;
  className?: string;
}

/**
 * A tabular-numerals counter that animates from 0 → ``value`` with an
 * ease-out curve. Built on Framer Motion's ``animate`` so the count is
 * GPU-friendly. Used for the lookup verdict's hit_count + total_debt
 * — register amounts feel weightier when they tick up to their
 * value than when they pop in pre-rendered.
 */
export function NumberCounter({
  value,
  decimals = 0,
  group = true,
  duration = 0.6,
  className,
}: NumberCounterProps) {
  const motionValue = useMotionValue(0);
  const display = useTransform(motionValue, (latest) => {
    const fixed = latest.toFixed(decimals);
    if (!group) return fixed;
    const [whole, frac] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' '); // narrow no-break space
    return frac ? `${grouped},${frac}` : grouped;
  });

  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration,
      ease: [0.22, 1, 0.36, 1], // ease-out-quart
    });
    return controls.stop;
  }, [value, duration, motionValue]);

  return <motion.span className={className}>{display}</motion.span>;
}
